import { ChatMessage } from '../../models/chat-message';
import { ChatMessageChunk } from '../../models/chat-message-chunk';

/**
 * Tracker for assembling chunks into complete messages
 */
export interface ChunkTracker {
  chunks: ChatMessageChunk[];
  completeReceived: boolean;
  createdAt: number;
  completedAt?: number;
}

/**
 * Information about a message being tracked in the registry
 */
export interface MessageInfo {
  botMessage: ChatMessage;
  tracker: ChunkTracker;
  status: 'active' | 'completed';
  completedAt?: number;
}

/**
 * Registry for managing message-based tracking of streaming chunks.
 * This follows industry patterns from Netflix, Kafka, and distributed streaming systems
 * to handle out-of-order chunk delivery gracefully.
 */
export class MessageRegistry {
  private registry = new Map<string, MessageInfo>();
  private cleanupInterval: any;

  // Metrics for monitoring
  private metrics = {
    totalMessages: 0,
    lateChunksProcessed: 0,
    averageLateChunkDelay: 0,
    duplicatesPreventedCount: 0,
    messagesCleanedUp: 0
  };

  constructor(
    private logger: any,
    private cleanupIntervalMs: number = 60000, // 1 minute
    private retentionMs: number = 5 * 60 * 1000 // 5 minutes
  ) {
    this.startCleanupInterval();
  }

  /**
   * Get or create a message info entry for the given messageId
   */
  getOrCreateMessage(
    messageId: string,
    createFn: () => ChatMessage
  ): MessageInfo {
    let messageInfo = this.registry.get(messageId);

    if (!messageInfo) {
      // Create new message entry
      const botMessage = createFn();
      botMessage.messageId = messageId; // Ensure messageId is set

      messageInfo = {
        botMessage,
        tracker: {
          chunks: [],
          completeReceived: false,
          createdAt: Date.now()
        },
        status: 'active'
      };

      this.registry.set(messageId, messageInfo);

      // Update metrics
      this.metrics.totalMessages++;

      this.logger.info('[MESSAGE-REGISTRY] Created new message entry', {
        messageId,
        registrySize: this.registry.size,
        totalMessagesCreated: this.metrics.totalMessages
      });
    } else if (messageInfo.status === 'completed') {
      // Late-arriving chunk for completed message
      const delayMs = Date.now() - (messageInfo.completedAt || 0);

      // Update late chunk metrics
      this.metrics.lateChunksProcessed++;
      this.metrics.duplicatesPreventedCount++;

      // Update average delay (simple moving average)
      this.metrics.averageLateChunkDelay =
        ((this.metrics.averageLateChunkDelay * (this.metrics.lateChunksProcessed - 1)) + delayMs)
        / this.metrics.lateChunksProcessed;

      this.logger.info('[MESSAGE-REGISTRY] Processing late chunk for completed message', {
        messageId,
        completedAt: messageInfo.completedAt,
        delayMs,
        totalLateChunks: this.metrics.lateChunksProcessed,
        averageDelay: this.metrics.averageLateChunkDelay,
        duplicatesPrevented: this.metrics.duplicatesPreventedCount
      });
    }

    return messageInfo;
  }

  /**
   * Get existing message info, or null if not found
   */
  getMessage(messageId: string): MessageInfo | null {
    return this.registry.get(messageId) || null;
  }

  /**
   * Mark a message as completed
   */
  markCompleted(messageId: string): void {
    const messageInfo = this.registry.get(messageId);
    if (messageInfo) {
      messageInfo.status = 'completed';
      messageInfo.completedAt = Date.now();
      messageInfo.tracker.completedAt = Date.now();

      this.logger.info('[MESSAGE-REGISTRY] Message marked as completed', {
        messageId,
        chunkCount: messageInfo.tracker.chunks.length,
        duration: Date.now() - messageInfo.tracker.createdAt
      });
    }
  }

  /**
   * Add a chunk to the tracker for the given message
   * Uses binary search to insert in sorted order to avoid O(n² log n) sorting complexity
   */
  addChunk(messageId: string, chunk: ChatMessageChunk): void {
    const messageInfo = this.registry.get(messageId);
    if (messageInfo) {
      // Check for duplicate chunks by sequence number
      const existingChunk = messageInfo.tracker.chunks.find(c => c.sequenceNumber === chunk.sequenceNumber);
      if (existingChunk) {
        this.metrics.duplicatesPreventedCount++;
        this.logger.debug('[MESSAGE-REGISTRY] Duplicate chunk prevented', {
          messageId,
          sequenceNumber: chunk.sequenceNumber,
          totalDuplicatesPrevented: this.metrics.duplicatesPreventedCount
        });
        return; // Skip duplicate chunk
      }

      // Binary search to find insertion point - maintains sorted order
      // This avoids re-sorting the entire array on every chunk update (O(n log n) → O(log n))
      let left = 0;
      let right = messageInfo.tracker.chunks.length;

      while (left < right) {
        const mid = Math.floor((left + right) / 2);
        if (messageInfo.tracker.chunks[mid].sequenceNumber < chunk.sequenceNumber) {
          left = mid + 1;
        } else {
          right = mid;
        }
      }

      // Insert chunk at the correct sorted position
      messageInfo.tracker.chunks.splice(left, 0, chunk);

      if (chunk.complete) {
        messageInfo.tracker.completeReceived = true;
      }
    }
  }

  /**
   * Get all registry entries (for debugging)
   */
  getAllEntries(): Map<string, MessageInfo> {
    return new Map(this.registry);
  }

  /**
   * Get registry statistics
   */
  getStats(): {
    totalMessages: number;
    activeMessages: number;
    completedMessages: number;
    oldestMessageAge: number;
  } {
    let activeCount = 0;
    let completedCount = 0;
    let oldestAge = 0;
    const now = Date.now();

    for (const messageInfo of this.registry.values()) {
      if (messageInfo.status === 'active') {
        activeCount++;
      } else {
        completedCount++;
      }

      const age = now - messageInfo.tracker.createdAt;
      if (age > oldestAge) {
        oldestAge = age;
      }
    }

    return {
      totalMessages: this.registry.size,
      activeMessages: activeCount,
      completedMessages: completedCount,
      oldestMessageAge: oldestAge
    };
  }

  /**
   * Get metrics for monitoring late chunk frequency and performance
   */
  getMetrics(): {
    totalMessages: number;
    lateChunksProcessed: number;
    averageLateChunkDelay: number;
    duplicatesPreventedCount: number;
    messagesCleanedUp: number;
    lateChunkRate: number; // percentage of messages that had late chunks
  } {
    const lateChunkRate = this.metrics.totalMessages > 0
      ? (this.metrics.lateChunksProcessed / this.metrics.totalMessages) * 100
      : 0;

    return {
      ...this.metrics,
      lateChunkRate
    };
  }

  /**
   * Clean up old completed messages
   */
  private cleanupOldMessages(): void {
    const cutoff = Date.now() - this.retentionMs;
    let cleanedCount = 0;

    for (const [messageId, messageInfo] of this.registry) {
      if (messageInfo.status === 'completed' &&
          messageInfo.completedAt &&
          messageInfo.completedAt < cutoff) {
        this.registry.delete(messageId);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      // Update cleanup metrics
      this.metrics.messagesCleanedUp += cleanedCount;

      this.logger.info('[MESSAGE-REGISTRY] Cleaned up old completed messages', {
        cleanedCount,
        remainingMessages: this.registry.size,
        retentionMs: this.retentionMs,
        totalCleaned: this.metrics.messagesCleanedUp
      });
    }
  }

  /**
   * Start the periodic cleanup interval
   */
  private startCleanupInterval(): void {
    this.cleanupInterval = setInterval(() => {
      this.cleanupOldMessages();
    }, this.cleanupIntervalMs);
  }

  /**
   * Manually clear all entries (for reset/clear operations)
   */
  public cleanup(): void {
    // Complete any BehaviorSubjects in messages before clearing
    for (const messageInfo of this.registry.values()) {
      try {
        messageInfo.botMessage.content.complete();
      } catch (e) {
        // Ignore errors from already completed subjects
      }
    }

    this.registry.clear();
    this.logger.info('[MESSAGE-REGISTRY] Manual cleanup - all entries cleared');
  }

  /**
   * Stop the cleanup interval and clear all entries
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    this.cleanup();

    this.logger.info('[MESSAGE-REGISTRY] Registry destroyed');
  }
}